import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Clock, Package, Warning } from 'shared/icons';
import api from '../../../services/api';
import { FormatInt } from '../../../utils/format';
import Badge from '../../ui/Badge';
import Button from '../../ui/Button';
import { Mono } from '../../ui/Mono';
import { useToast } from '../../ui/Toast';
import BlockTower from './BlockTower';
import {
    BigAction,
    CodeLine,
    DangerScreenAction,
    FailureScreen,
    PlainAddress,
    ScanFlash,
    StepShell,
    SuccessScreen,
    WaitScreen,
} from './CollectorChrome';
import PauseServiceOrderPanel from './PauseServiceOrderPanel';
import ScanInput from './ScanInput';
import SkipItemPanel from './SkipItemPanel';
import { PlayMovementDone, PlayScanError, PlayScanSuccess } from './collectorFeedback';
import {
    CodesMatch,
    ExpectedScanCodeFor,
    FormatLocationPlain,
    IsRackLocation,
    ParseEngineBlocks,
    ParseScanResult,
    SCAN_METHOD_SCANNED,
    SCAN_METHOD_TYPED,
    SCAN_TYPE_BLOCK,
    SCAN_TYPE_LOCATION,
} from './collectorScan';
import {
    HasFreeDestination,
    ITEM_STEP_DESTINATION,
    ITEM_STEP_LPN,
    ITEM_STEP_ORIGIN,
    ITEM_STEP_STAGING,
    ITEM_STEP_UNITS,
    IsFractionalItem,
    IsMovementBlockedError,
    IsOperatorMismatchError,
    IsStaleStateError,
    NextServiceOrderItem,
    ParseOriginMismatch,
    ParsePairWait,
    ParseServiceOrder,
    SERVICE_ORDER_STATUS_CANCELED,
    SERVICE_ORDER_STATUS_DONE,
    SERVICE_ORDER_STATUS_PAUSED,
    IsLastPendingItem,
    ItemUnitCount,
    ServiceOrderItemLocation,
    StagingStepCode,
    ServiceOrderItemSteps,
    ServiceOrderProgress,
    UNIT_SCAN_COOLDOWN_MS,
} from './collectorServiceOrders';

/**
 * EXECUTAR A OS — item a item, na gramática que o operador já aprendeu no M4.
 *
 * ── O que muda do movimento avulso (e por que a tela parece a mesma) ────────
 * No M4 o operador DEFINIA o movimento bipando: a origem era onde ele estava, o
 * destino era escolha dele. Aqui a rota já foi decidida — o sistema sabe as
 * três strings antes de ele sair andando. A tela é deliberadamente a mesma
 * (título grande, endereço falado, torre com o alvo aceso, esperado × lido em
 * vermelho; "Digitar" só no console — no quiosque /coletor o terminal só bipa,
 * por ordem do dono de 26/09, ver `ScanInput`) porque trocar o vocabulário
 * visual entre dois trabalhos do mesmo turno é o jeito mais barato de criar
 * erro; o que muda é que agora TODO passo tem código esperado explícito,
 * inclusive a origem.
 *
 * Consequência prática: os três passos validam por igualdade exata LOCAL, sem
 * ida ao servidor. A gravação é uma chamada só, no fim do item — e é ela que
 * pode dizer "espere o par", "a regra do armazém barrou" ou "o LPN não está
 * onde você diz".
 *
 * ── Os três desfechos de um bipe de destino ─────────────────────────────────
 *   • 200 → item `DONE`, a tela pula para o próximo item da rota.
 *   • 409 do MOTOR (§6.1) → tela VERMELHA com os motivos: isso é erro.
 *   • 409 de SINCRONIA (§6.5) → tela ÂMBAR de ESPERA: a outra metade do par
 *     ainda não entregou. Ninguém errou; a ação certa é aguardar, e insistir
 *     é um botão, não uma bronca.
 *
 * ── Onde fica o "pular" ─────────────────────────────────────────────────────
 * Em TODA tela — passo, vermelha e espera —, sempre como ação secundária que
 * abre a tela de confirmação com motivo obrigatório (`SkipItemPanel`). Visível
 * porque é a válvula que impede a rota de travar; nunca no botão grande porque
 * pular tem custo (incidente aberto, alguém resolve depois).
 *
 * ── A torre vem DEPOIS do bipe (regra do dono, 26/09) ───────────────────────
 * Nenhum passo desenha a torre antes de o operador bipar o bloco: na origem
 * ela aparece no passo do LPN (a origem já foi bipada); no destino, só depois
 * do bipe do destino. Destino PLANEJADO: bipou o bloco certo → torre com a
 * posição acesa + "Guardei em…". Destino LIVRE (item sem destino — o operador
 * escolhe onde guardar): bipou um bloco → torre para escolher a posição livre
 * → conferência; bipou um endereço fora do rack → conferência direto.
 */

// Sub-fases do passo do DESTINO: bipar → (escolher na torre) → conferir.
const DESTINATION_PHASE_SCAN = 'SCAN';
const DESTINATION_PHASE_PICK = 'PICK';
const DESTINATION_PHASE_CONFIRM = 'CONFIRM';

const SCREEN_STEP = 'STEP';
const SCREEN_MISMATCH = 'MISMATCH';
const SCREEN_BLOCKED = 'BLOCKED';
const SCREEN_FAILED = 'FAILED';
const SCREEN_WAIT_PAIR = 'WAIT_PAIR';
const SCREEN_PAUSE = 'PAUSE';
const SCREEN_SKIP = 'SKIP';

const ServiceOrderFlow = ({ label, operator, order: initialOrder, onExit }) => {
    const toast = useToast();
    const [order, setOrder] = useState(initialOrder || null);
    const [loading, setLoading] = useState(!initialOrder?.items);
    const [loadError, setLoadError] = useState(null);
    const [screen, setScreen] = useState(SCREEN_STEP);
    const [step, setStep] = useState(ITEM_STEP_ORIGIN);
    const [lpnCode, setLpnCode] = useState(null);
    const [typedInItem, setTypedInItem] = useState(false);
    const [mismatch, setMismatch] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [pairWait, setPairWait] = useState(null);
    const [failure, setFailure] = useState(null);
    const [resumeError, setResumeError] = useState(null);
    const [tower, setTower] = useState(null);
    // Passo das unidades: quantas já foram separadas e a espera entre bipes.
    const [unitsPicked, setUnitsPicked] = useState(0);
    const [unitWaitMs, setUnitWaitMs] = useState(0);
    const [busy, setBusy] = useState(false);
    const [flash, setFlash] = useState(null);
    // Passo do DESTINO: a sub-fase, a torre do bloco bipado (destino livre,
    // modo escolha) e o destino escolhido/confirmado que vai no execute.
    const [destinationPhase, setDestinationPhase] = useState(DESTINATION_PHASE_SCAN);
    const [destinationPick, setDestinationPick] = useState(null);
    const [destinationChoice, setDestinationChoice] = useState(null);
    const aliveRef = useRef(true);
    const towerCacheRef = useRef(new Map());

    const orderId = initialOrder?.id || order?.id || null;
    const item = NextServiceOrderItem(order);
    const status = String(order?.status || '').toUpperCase();
    const from = ServiceOrderItemLocation(item, ITEM_STEP_ORIGIN);
    const to = ServiceOrderItemLocation(item, ITEM_STEP_DESTINATION);
    const freeDestination = HasFreeDestination(item);
    const fractional = IsFractionalItem(item);
    const unitTarget = ItemUnitCount(item);
    // O destino só entra no ÚLTIMO item: o carrinho vai ao packing uma vez.
    const ultimoItem = IsLastPendingItem(order, item);
    const itemSteps = ServiceOrderItemSteps(item, { ultimoItem });
    // Item sem origem começa no LPN — o primeiro passo é do item, não fixo.
    const firstStep = itemSteps[0] || ITEM_STEP_ORIGIN;
    // O endereço que a tela MOSTRA: a origem guia os passos de origem, pallet e
    // unidades (o operador está de pé na frente dela); o destino guia o último.
    const stepLocation = step === ITEM_STEP_DESTINATION ? to : from;
    // O que o BIPE tem que casar: endereço nos passos de lugar, LPN no passo do
    // pallet. No passo das unidades quem confere é o servidor (o código de
    // barras da unidade vira produto), então não há esperado local.
    const expectedCode =
        step === ITEM_STEP_ORIGIN || step === ITEM_STEP_DESTINATION
            ? stepLocation
                ? ExpectedScanCodeFor(stepLocation)
                : null
            : step === ITEM_STEP_LPN
              ? item?.lpn_code
              : step === ITEM_STEP_STAGING
                ? StagingStepCode(item)
                : null;

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    const reload = useCallback(async () => {
        if (!orderId) {
            return;
        }
        setLoading(true);
        try {
            const payload = await api.getWarehouseServiceOrder(orderId);
            if (!aliveRef.current) {
                return;
            }
            setOrder(ParseServiceOrder(payload));
            setLoadError(null);
            setScreen(SCREEN_STEP);
        } catch (error) {
            if (aliveRef.current) {
                setLoadError(error?.message || label('wms.common.load_error'));
            }
        } finally {
            if (aliveRef.current) {
                setLoading(false);
            }
        }
    }, [label, orderId]);

    // A ficha completa (itens + pausas) é o que o fluxo executa. O aceite já
    // devolve tudo — quando a OS veio da lista como resumo (o "Continuar" da OS
    // ativa), busca aqui.
    useEffect(() => {
        if (!initialOrder?.items) {
            reload();
        }
    }, [initialOrder, reload]);

    // Trocou de item = cadeia nova: passo volta ao primeiro (a origem, ou o LPN
    // quando o item não tem origem), o pallet sai da mão e o "alguém digitou"
    // zera (a honestidade do `scan_method` é por ITEM, não por OS — um item
    // digitado não pode contaminar a trilha do seguinte).
    const currentItemId = item?.id || null;
    useEffect(() => {
        setStep(firstStep);
        setLpnCode(null);
        setTypedInItem(false);
        setUnitsPicked(0);
        setUnitWaitMs(0);
        setDestinationPhase(DESTINATION_PHASE_SCAN);
        setDestinationPick(null);
        setDestinationChoice(null);
    }, [currentItemId, firstStep]);

    // A espera entre unidades corre na tela (o operador vê quanto falta em vez
    // de bipar num campo morto sem entender por quê).
    useEffect(() => {
        if (unitWaitMs <= 0) {
            return undefined;
        }
        const timer = setInterval(() => {
            setUnitWaitMs((current) => (current <= 250 ? 0 : current - 250));
        }, 250);
        return () => clearInterval(timer);
    }, [unitWaitMs]);

    // A torre do bloco: o alvo aceso é o que o operador confere de longe, mas o
    // item traz só o endereço — as posições vizinhas vêm do classificador
    // (§2.2). É enfeite funcional, então falha em silêncio: sem torre, o
    // endereço falado e o código esperado seguem guiando o passo.
    //
    // ⚠️ A dependência é o CÓDIGO DO BLOCO (string), não o objeto do endereço:
    // `ServiceOrderItemLocation` remonta o objeto a cada render, e um objeto na
    // lista de dependências faria este efeito rodar sem parar.
    const towerBlockCode = stepLocation && IsRackLocation(stepLocation) ? ExpectedScanCodeFor(stepLocation) : null;
    const towerTargetCode = step === ITEM_STEP_DESTINATION ? to?.code : from?.code;
    useEffect(() => {
        if (!towerBlockCode) {
            setTower(null);
            return undefined;
        }
        const cached = towerCacheRef.current.get(towerBlockCode);
        if (cached) {
            setTower(cached);
            return undefined;
        }

        let active = true;
        setTower(null);
        api.scanWarehouseCode(towerBlockCode)
            .then((payload) => {
                const parsed = ParseScanResult(payload);
                if (!parsed.block || parsed.positions.length === 0) {
                    return;
                }
                const value = { ...parsed.block, positions: parsed.positions };
                towerCacheRef.current.set(towerBlockCode, value);
                if (active && aliveRef.current) {
                    setTower(value);
                }
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [towerBlockCode]);

    const plain = useCallback((location) => FormatLocationPlain(location, { label }), [label]);
    // No passo de BIPE o endereço falado é o do BLOCO ("Rack A, Bloco 1") — é a
    // etiqueta física que existe. Andar/posição são escolhidos na torre.
    const plainScan = useCallback((location) => FormatLocationPlain(location, { label, full: false }), [label]);

    const fail = useCallback((payload) => {
        PlayScanError();
        setMismatch(payload);
        setScreen(SCREEN_MISMATCH);
    }, []);

    /** Grava o item: é aqui que a OS vira movimento na trilha. */
    const execute = useCallback(
        async ({ typed = false, lpn = null, destinationCode = null } = {}) => {
            const lpnForItem = lpn || lpnCode || item?.lpn_code || null;
            // O destino que vai no execute: o PLANEJADO, ou — no destino livre —
            // a posição que o operador escolheu (bipe do endereço ou toque na
            // torre depois do bipe do bloco).
            const destinationForItem = destinationCode || destinationChoice?.location?.code || to?.code || null;
            if (!item || !destinationForItem || !lpnForItem) {
                return;
            }
            setBusy(true);
            try {
                const payload = await api.executeWarehouseServiceOrderItem(order.id, item.id, {
                    // QUEM FEZ: o crachá da sessão. A conta do coletor é do
                    // DISPOSITIVO — sem este campo a trilha atribuiria à
                    // máquina o trabalho de uma pessoa.
                    operator_code: operator?.employee_code || undefined,
                    // Pior caso da cadeia: um único passo digitado já torna o
                    // item TYPED (mesma regra do M4).
                    scan_method: typed || typedInItem ? SCAN_METHOD_TYPED : SCAN_METHOD_SCANNED,
                    lpn_code: lpnForItem,
                    // Os códigos do PLANO, não os do bipe: no rack o operador
                    // bipa o BLOCO (única etiqueta física), e quem sabe a
                    // posição exata é a OS — ou a torre, no destino livre.
                    to_location_code: destinationForItem,
                    from_location_code: from?.code || undefined,
                });
                PlayMovementDone();
                const next = ParseServiceOrder(payload);
                if (next) {
                    setOrder(next);
                    setScreen(SCREEN_STEP);
                } else {
                    await reload();
                }
                setFlash(label('wms.collector.item_done_flash'));
            } catch (error) {
                PlayScanError();

                // A ORDEM importa: o bloqueio do motor tem código e lista
                // próprios, então ele é reconhecido ANTES da heurística de
                // espera do par — que é generosa de propósito, porque o
                // formato do 409 de sincronia ainda está sendo escrito.
                if (IsMovementBlockedError(error)) {
                    setBlocks(ParseEngineBlocks(error));
                    setScreen(SCREEN_BLOCKED);
                    return;
                }
                const waiting = ParsePairWait(error);
                if (waiting) {
                    setPairWait(waiting);
                    setScreen(SCREEN_WAIT_PAIR);
                    return;
                }
                const originMismatch = ParseOriginMismatch(error);
                if (originMismatch) {
                    setMismatch({
                        title: label('wms.collector.mismatch_place_title'),
                        message: error.message || label('wms.collector.mismatch_place_hint'),
                        read: originMismatch.read,
                        canReportMismatch: false,
                    });
                    setScreen(SCREEN_MISMATCH);
                    return;
                }
                setFailure({
                    message: error?.message || label('wms.common.load_error'),
                    // Estado velho na tela: recarregar é o que resolve, e é o
                    // que a tela oferece como saída principal.
                    stale: IsStaleStateError(error) || IsOperatorMismatchError(error),
                });
                setScreen(SCREEN_FAILED);
            } finally {
                if (aliveRef.current) {
                    setBusy(false);
                }
            }
        },
        [destinationChoice, from, item, label, lpnCode, operator, order, reload, to, typedInItem],
    );

    /** O passo seguinte DESTE item (fracionado tem o passo das unidades). */
    const advance = useCallback(
        (fromStep) => {
            const steps = ServiceOrderItemSteps(item, { ultimoItem });
            const next = steps[steps.indexOf(fromStep) + 1];
            if (next) {
                setStep(next);
                return;
            }
            // Sem próximo passo: o item terminou aqui. É o caso do fracionado
            // que NÃO é o último — as unidades estão no carrinho e o destino
            // fica para a entrega, no fim da OS.
            execute({ lpn: lpnCode });
        },
        [execute, item, lpnCode, ultimoItem],
    );

    /**
     * UMA UNIDADE — o bipe do código de barras do produto, 1 a 1.
     *
     * Quem confere é o servidor (`scanWarehouseProduct`): o código de barras
     * pode ser EAN ou SKU, e a tela não tem catálogo. Acertou: conta, dá o
     * flash e ABRE UMA ESPERA — leitor com toque longo lê a mesma etiqueta duas
     * vezes, e contar 2 por 1 é pior do que esperar 3 segundos.
     */
    const handleUnitScan = useCallback(
        async (code) => {
            setBusy(true);
            try {
                const scan = await api.scanWarehouseProduct(code);
                const candidates = [scan?.product, ...(Array.isArray(scan?.candidates) ? scan.candidates : [])];
                // O DTO do scan fala `product_id` (wire snake_case) — não `id`.
                const matched = candidates.some((row) => row && row.product_id === item?.product_id);
                if (!matched) {
                    fail({
                        title: label(
                            scan?.status === 'NOT_FOUND'
                                ? 'wms.collector.unit_not_found'
                                : 'wms.collector.unit_wrong_product',
                        ),
                        message: label(
                            scan?.status === 'NOT_FOUND'
                                ? 'wms.collector.unit_not_found_hint'
                                : 'wms.collector.unit_wrong_product_hint',
                        ),
                        read: code,
                        canReportMismatch: false,
                    });
                    return;
                }
                PlayScanSuccess();
                const picked = unitsPicked + 1;
                setUnitsPicked(picked);
                if (picked >= (unitTarget ?? 0)) {
                    setFlash(label('wms.collector.units_complete'));
                    advance(ITEM_STEP_UNITS);
                    return;
                }
                setFlash(label('wms.collector.unit_reserved'));
                setUnitWaitMs(UNIT_SCAN_COOLDOWN_MS);
            } catch (error) {
                fail({
                    title: label('wms.collector.scan_failed_title'),
                    message: error?.message || label('wms.common.load_error'),
                    read: code,
                    canReportMismatch: false,
                });
            } finally {
                if (aliveRef.current) {
                    setBusy(false);
                }
            }
        },
        [advance, fail, item, label, unitTarget, unitsPicked],
    );

    /**
     * DESTINO LIVRE — o bipe define ONDE guardar (mesma leitura do movimento
     * avulso): bloco → torre para escolher a posição livre; endereço fora do
     * rack → conferência direto. VIRTUAL não é lugar de guardar pallet.
     * Quem cobra as travas do armazém (§6.1) é o execute, na conferência.
     */
    const handleFreeDestinationScan = useCallback(
        async (code) => {
            setBusy(true);
            try {
                const parsed = ParseScanResult(await api.scanWarehouseCode(code));
                if (parsed.type === SCAN_TYPE_BLOCK && parsed.block && parsed.positions.length > 0) {
                    PlayScanSuccess();
                    setFlash(label('wms.collector.flash_block'));
                    setDestinationPick({ ...parsed.block, positions: parsed.positions });
                    setDestinationPhase(DESTINATION_PHASE_PICK);
                    return;
                }
                if (parsed.type === SCAN_TYPE_LOCATION && parsed.location) {
                    if (parsed.location.is_virtual || parsed.location.type === 'VIRTUAL') {
                        fail({
                            title: label('wms.collector.destination_virtual_title'),
                            message: label('wms.collector.destination_virtual_hint'),
                            read: code,
                            canReportMismatch: false,
                        });
                        return;
                    }
                    PlayScanSuccess();
                    setFlash(label('wms.collector.flash_destination'));
                    setDestinationChoice({ location: parsed.location, tower: null });
                    setDestinationPhase(DESTINATION_PHASE_CONFIRM);
                    return;
                }
                fail({
                    title: label('wms.collector.mismatch_type_title'),
                    message: label('wms.collector.mismatch_expected_location'),
                    read: code,
                    canReportMismatch: false,
                });
            } catch (error) {
                fail({
                    title: label('wms.collector.scan_failed_title'),
                    message: error?.message || label('wms.common.load_error'),
                    read: code,
                    canReportMismatch: false,
                });
            } finally {
                if (aliveRef.current) {
                    setBusy(false);
                }
            }
        },
        [fail, label],
    );

    /** Toque na torre do destino livre: a posição escolhida vai à conferência. */
    const handlePickDestination = useCallback(
        (row) => {
            if (!row?.code) {
                return;
            }
            PlayScanSuccess();
            setFlash(label('wms.collector.flash_destination'));
            // A linha da torre já é o DTO da posição (código, rack, bloco,
            // andar, posição) — é tudo o que a conferência fala e o execute leva.
            setDestinationChoice({ location: row, tower: destinationPick });
            setDestinationPick(null);
            setDestinationPhase(DESTINATION_PHASE_CONFIRM);
        },
        [destinationPick, label],
    );

    /** Volta ao bipe do destino (livre: "Trocar destino"). */
    const restartDestination = useCallback(() => {
        setDestinationPick(null);
        setDestinationChoice(null);
        setDestinationPhase(DESTINATION_PHASE_SCAN);
    }, []);

    const handleScan = useCallback(
        (code, scanMethod) => {
            const typed = scanMethod === SCAN_METHOD_TYPED;
            if (typed) {
                setTypedInItem(true);
            }

            if (step === ITEM_STEP_UNITS) {
                handleUnitScan(code);
                return;
            }

            // O "00": o pallet desceu. Conferido LOCALMENTE (a cabeceira do
            // rack de origem é conhecida) e sem gravar movimento — o pallet
            // está a caminho da doca, e um MOVE intermediário faria a trilha
            // dizer que ele ficou na cabeceira.
            if (step === ITEM_STEP_STAGING) {
                if (expectedCode && !CodesMatch(expectedCode, code)) {
                    fail({
                        title: label('wms.collector.mismatch_staging_title'),
                        message: label('wms.collector.staging_hint'),
                        read: code,
                        canReportMismatch: true,
                    });
                    return;
                }
                PlayScanSuccess();
                setFlash(label('wms.collector.flash_staging'));
                advance(ITEM_STEP_STAGING);
                return;
            }

            if (step === ITEM_STEP_ORIGIN) {
                if (expectedCode && !CodesMatch(expectedCode, code)) {
                    fail({
                        title: label('wms.collector.mismatch_destination_title'),
                        message: label('wms.collector.step_origin_hint'),
                        read: code,
                        canReportMismatch: true,
                    });
                    return;
                }
                PlayScanSuccess();
                setFlash(label('wms.collector.flash_origin'));
                advance(ITEM_STEP_ORIGIN);
                return;
            }

            if (step === ITEM_STEP_LPN) {
                const expectedLpn = item?.lpn_code || null;
                if (expectedLpn && !CodesMatch(expectedLpn, code)) {
                    fail({
                        title: label('wms.collector.mismatch_lpn_title'),
                        message: label('wms.collector.mismatch_lpn_hint'),
                        read: code,
                        canReportMismatch: true,
                    });
                    return;
                }
                PlayScanSuccess();
                setFlash(label('wms.collector.flash_lpn'));
                setLpnCode(expectedLpn || code);
                advance(ITEM_STEP_LPN);
                return;
            }

            // ── DESTINO ──
            if (freeDestination) {
                handleFreeDestinationScan(code);
                return;
            }
            if (expectedCode && !CodesMatch(expectedCode, code)) {
                fail({
                    title: label('wms.collector.mismatch_destination_title'),
                    message: label('wms.collector.step_destination_hint'),
                    read: code,
                    canReportMismatch: true,
                });
                return;
            }
            PlayScanSuccess();
            // No RACK o bipe provou o BLOCO, não a posição: só agora a torre
            // aparece, com a posição do plano acesa, e o operador confirma que
            // guardou nela.
            if (to && IsRackLocation(to)) {
                setFlash(label('wms.collector.flash_block'));
                setDestinationChoice({ location: to, tower: null });
                setDestinationPhase(DESTINATION_PHASE_CONFIRM);
                return;
            }
            setFlash(label('wms.collector.flash_destination'));
            // Fora do rack a etiqueta É o endereço inteiro: o bipe do destino
            // é a confirmação (o pallet já está encostado no lugar).
            execute({ typed, lpn: lpnCode });
        },
        [
            advance,
            execute,
            expectedCode,
            fail,
            freeDestination,
            handleFreeDestinationScan,
            handleUnitScan,
            item,
            label,
            lpnCode,
            step,
            to,
        ],
    );

    const handleResume = useCallback(async () => {
        setBusy(true);
        setResumeError(null);
        try {
            const payload = await api.resumeWarehouseServiceOrder(order.id, operator?.employee_code);
            PlayScanSuccess();
            setOrder(ParseServiceOrder(payload));
            setScreen(SCREEN_STEP);
        } catch (error) {
            PlayScanError();
            // Crachá diferente do de quem pausou é a regra funcionando, não uma
            // falha: a frase do backend já diz de quem é a OS.
            setResumeError(error?.message || label('wms.common.load_error'));
        } finally {
            if (aliveRef.current) {
                setBusy(false);
            }
        }
    }, [label, operator, order]);

    const progress = ServiceOrderProgress(order);
    const flashNode = <ScanFlash message={flash} onDone={() => setFlash(null)} />;

    const openSkip = () => setScreen(SCREEN_SKIP);
    const openPause = () => setScreen(SCREEN_PAUSE);
    const backToStep = () => {
        setMismatch(null);
        setBlocks([]);
        setPairWait(null);
        setFailure(null);
        setScreen(SCREEN_STEP);
    };
    // "Tentar de novo" depois de uma tela vermelha: no destino, recomeça pelo
    // BIPE — a posição recusada (ocupada, regra do armazém) não pode continuar
    // escolhida, e a torre só volta depois de um bipe novo.
    const retryStep = () => {
        restartDestination();
        backToStep();
    };

    // ── Sem ficha não há execução ───────────────────────────────────────────
    // Vale tanto para "ainda carregando" quanto para "a carga falhou": sem a
    // lista de itens não existe próximo item, e seguir daqui mostraria a tela
    // de OS concluída para uma OS que só não foi lida — o pior erro possível
    // nesta tela.
    if (!order || !Array.isArray(order.items)) {
        return (
            <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 text-center">
                <Mono className="text-2xl font-black text-black dark:text-white">
                    {order?.code || initialOrder?.code || ''}
                </Mono>
                <p className="text-lg text-mono-500 dark:text-mono-400">
                    {loadError || (loading ? label('wms.collector.loading_order') : label('wms.common.load_error'))}
                </p>
                <Button variant="secondary" size="lg" onClick={reload} loading={loading}>
                    {label('wms.common.retry')}
                </Button>
                <Button variant="ghost" size="lg" onClick={onExit}>
                    {label('wms.collector.back_to_orders')}
                </Button>
            </div>
        );
    }

    // ── Painéis que tomam a tela inteira ────────────────────────────────────
    if (screen === SCREEN_PAUSE) {
        return (
            <PauseServiceOrderPanel
                label={label}
                order={order}
                operator={operator}
                // O que a TELA sabe sobre carga na mão: o LPN já foi bipado e o
                // destino ainda não. O backend tem a própria fonte (item
                // EXECUTING) e manda nela.
                carryingLpnCode={step === ITEM_STEP_DESTINATION ? lpnCode : null}
                onCancel={backToStep}
                onPaused={(paused) => {
                    setScreen(SCREEN_STEP);
                    if (paused) {
                        setOrder(paused);
                    } else {
                        reload();
                    }
                }}
            />
        );
    }

    if (screen === SCREEN_SKIP && item) {
        return (
            <SkipItemPanel
                label={label}
                order={order}
                item={item}
                operator={operator}
                onCancel={backToStep}
                onSkipped={(skipped) => {
                    toast.success(label('wms.collector.skip_done'));
                    setScreen(SCREEN_STEP);
                    if (skipped) {
                        setOrder(skipped);
                    } else {
                        reload();
                    }
                }}
            />
        );
    }

    // ── Estados terminais da OS ─────────────────────────────────────────────
    if (status === SERVICE_ORDER_STATUS_CANCELED) {
        return (
            <FailureScreen
                label={label}
                title={label('wms.collector.order_canceled_title')}
                message={label('wms.collector.order_canceled_hint')}
                actions={
                    <BigAction variant="secondary" onClick={onExit}>
                        {label('wms.collector.back_to_orders')}
                    </BigAction>
                }
            />
        );
    }

    if (status === SERVICE_ORDER_STATUS_DONE || !item) {
        return (
            <>
                {flashNode}
                <SuccessScreen
                    title={label('wms.collector.order_done_title')}
                    actions={
                        <>
                            <BigAction onClick={onExit}>{label('wms.collector.back_to_orders')}</BigAction>
                            {/* Sem item pendente e a OS ainda aberta = a nossa
                                cópia envelheceu (o fechamento é do servidor).
                                Recarregar mostra o estado real em vez de deixar
                                o operador achando que ficou algo por fazer. */}
                            {status === SERVICE_ORDER_STATUS_DONE ? null : (
                                <Button variant="secondary" size="lg" className="w-full" onClick={reload}>
                                    {label('wms.collector.order_reload')}
                                </Button>
                            )}
                        </>
                    }
                >
                    <div className="space-y-3 text-center">
                        <Mono className="block text-3xl font-black text-black dark:text-white">{order.code}</Mono>
                        <Badge variant={status === SERVICE_ORDER_STATUS_DONE ? 'success' : 'info'}>
                            {label(`wms.service_order.status.${order.status}`)}
                        </Badge>
                        <div className="flex items-center justify-center gap-3 text-base font-bold text-black dark:text-white">
                            <span>{`${label('wms.collector.items_done')}: ${FormatInt(progress.done)}`}</span>
                            {progress.skipped > 0 ? (
                                <span className="text-warning-text dark:text-warning-300">
                                    {`${label('wms.collector.items_skipped')}: ${FormatInt(progress.skipped)}`}
                                </span>
                            ) : null}
                        </div>
                        {progress.skipped > 0 ? (
                            <p className="text-sm text-mono-600 dark:text-mono-400">
                                {label('wms.service_order_item.skipped_help')}
                            </p>
                        ) : null}
                    </div>
                </SuccessScreen>
            </>
        );
    }

    if (status === SERVICE_ORDER_STATUS_PAUSED) {
        const openPauseRow = (Array.isArray(order.pauses) ? order.pauses : []).find((row) => !row.resumed_at);
        return (
            <div className="flex min-h-[70vh] flex-col gap-5 rounded-2xl border-2 border-mono-300 bg-mono-50 p-5 dark:border-mono-700 dark:bg-mono-900">
                <div className="flex flex-1 flex-col justify-center gap-4 text-center">
                    <Clock size={64} className="mx-auto text-mono-500 dark:text-mono-400" />
                    <h2 className="text-3xl font-black text-black dark:text-white">
                        {label('wms.collector.paused_title')}
                    </h2>
                    <Mono className="block text-2xl font-black text-black dark:text-white">{order.code}</Mono>
                    {openPauseRow?.reason ? (
                        <p className="text-lg font-semibold text-mono-700 dark:text-mono-300">
                            {`${label('wms.pause.reason')}: ${label(`wms.pause.reason.${openPauseRow.reason}`)}`}
                        </p>
                    ) : null}
                    <p className="text-base text-mono-600 dark:text-mono-400">
                        {label('wms.pause.resume_same_operator')}
                    </p>
                    {resumeError ? (
                        <p
                            role="alert"
                            className="rounded-xl bg-danger-soft px-3 py-2 text-sm font-medium text-danger-text dark:bg-danger-900/30 dark:text-danger-300"
                        >
                            {resumeError}
                        </p>
                    ) : null}
                </div>
                <div className="sticky bottom-0 space-y-2">
                    <BigAction onClick={handleResume} loading={busy}>
                        {label('wms.pause.resume')}
                    </BigAction>
                    <Button variant="ghost" size="lg" className="w-full" onClick={onExit}>
                        <ArrowLeft size={20} className="mr-2" />
                        {label('wms.collector.back_to_orders')}
                    </Button>
                </div>
            </div>
        );
    }

    // ── Exceções do item ────────────────────────────────────────────────────
    const skipAction = (
        <DangerScreenAction onClick={openSkip} disabled={busy}>
            {label('wms.collector.skip_action')}
        </DangerScreenAction>
    );

    if (screen === SCREEN_MISMATCH) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={mismatch?.title}
                    message={mismatch?.message}
                    read={mismatch?.read}
                    actions={
                        <>
                            <BigAction variant="secondary" onClick={retryStep}>
                                {label('wms.collector.try_again')}
                            </BigAction>
                            {skipAction}
                        </>
                    }
                />
            </>
        );
    }

    if (screen === SCREEN_BLOCKED) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={label('wms.collector.blocked_title')}
                    message={label('wms.collector.blocked_hint')}
                    reasons={blocks}
                    actions={
                        <>
                            <BigAction variant="secondary" onClick={retryStep}>
                                {freeDestination
                                    ? label('wms.collector.pick_other_destination')
                                    : label('wms.collector.try_again')}
                            </BigAction>
                            {skipAction}
                        </>
                    }
                />
            </>
        );
    }

    if (screen === SCREEN_FAILED) {
        return (
            <>
                {flashNode}
                <FailureScreen
                    label={label}
                    title={label('wms.collector.execute_failed_title')}
                    message={failure?.message}
                    actions={
                        <>
                            <BigAction variant="secondary" onClick={failure?.stale ? reload : retryStep}>
                                {failure?.stale
                                    ? label('wms.collector.order_reload')
                                    : label('wms.collector.try_again')}
                            </BigAction>
                            {skipAction}
                            <DangerScreenAction onClick={onExit}>
                                {label('wms.collector.back_to_orders')}
                            </DangerScreenAction>
                        </>
                    }
                />
            </>
        );
    }

    if (screen === SCREEN_WAIT_PAIR) {
        return (
            <>
                {flashNode}
                <WaitScreen
                    title={label('wms.collector.waiting_pair')}
                    message={label('wms.collector.waiting_pair_help')}
                    actions={
                        <>
                            <BigAction onClick={() => execute({ lpn: lpnCode })} loading={busy}>
                                {label('wms.collector.pair_retry')}
                            </BigAction>
                            <Button variant="secondary" size="lg" className="w-full" onClick={openPause}>
                                {label('wms.collector.pause_action')}
                            </Button>
                            <Button variant="ghost" size="lg" className="w-full" onClick={openSkip}>
                                {label('wms.collector.skip_action')}
                            </Button>
                        </>
                    }
                >
                    {/* O QUE FALTA — a diferença entre uma espera com fim à
                        vista e uma tela que só manda esperar. Vem do 409; se o
                        backend não mandar detalhe, o texto de ajuda sozinho já
                        explica o mecanismo. */}
                    {pairWait?.pairCode || pairWait?.pairSeq || pairWait?.locationCode ? (
                        <div className="space-y-2 rounded-xl bg-white/70 px-4 py-3 text-center dark:bg-mono-950/50">
                            <p className="text-xs uppercase tracking-wide text-warning-text opacity-80 dark:text-warning-300">
                                {label('wms.collector.pair_missing')}
                            </p>
                            {pairWait.pairCode ? (
                                <Mono className="block text-xl font-black text-black dark:text-white">
                                    {pairWait.pairCode}
                                </Mono>
                            ) : null}
                            {pairWait.pairSeq ? (
                                <p className="text-sm font-semibold text-black dark:text-white">
                                    {`${label('wms.collector.pair_item')}: ${pairWait.pairSeq}`}
                                </p>
                            ) : null}
                            {pairWait.locationCode ? <CodeLine>{pairWait.locationCode}</CodeLine> : null}
                        </div>
                    ) : null}
                    {pairWait?.message ? (
                        <p className="text-center text-base font-medium text-warning-text dark:text-warning-200">
                            {pairWait.message}
                        </p>
                    ) : null}
                </WaitScreen>
            </>
        );
    }

    // ── O passo ─────────────────────────────────────────────────────────────
    const stepIndex = Math.max(0, itemSteps.indexOf(step));
    const stepName = `${label('wms.collector.item_counter')} ${progress.position} ${label(
        'wms.collector.item_of',
    )} ${progress.total} · ${label(`wms.collector.step_name.${step}`)}`;

    // Destino em conferência (depois do bipe — nunca antes): a posição e a
    // torre que a tela mostra, e o botão grande que grava o item.
    const onDestinationStep = step === ITEM_STEP_DESTINATION;
    const confirmingDestination =
        onDestinationStep && destinationPhase === DESTINATION_PHASE_CONFIRM && Boolean(destinationChoice?.location);
    const pickingDestination =
        onDestinationStep && destinationPhase === DESTINATION_PHASE_PICK && Boolean(destinationPick);
    const confirmLocation = confirmingDestination ? destinationChoice.location : null;
    // Torre da conferência: a do bloco bipado (livre) ou a do plano (carregada
    // pelo efeito da torre, que já mira o endereço do destino).
    const confirmTower = confirmingDestination ? (freeDestination ? destinationChoice.tower : tower) : null;
    // "Guardei em Andar 2, Posição 1" — o andar/posição é o que o operador
    // confere na torre; fora do rack, o código do endereço.
    const confirmLevel = confirmLocation?.level ?? null;
    const confirmSpot =
        confirmLevel !== null && confirmLevel !== undefined
            ? `${label('wms.field.level')} ${confirmLevel}${
                  confirmLocation?.position !== null && confirmLocation?.position !== undefined
                      ? `, ${label('wms.field.position')} ${confirmLocation.position}`
                      : ''
              }`
            : confirmLocation?.code || '';

    const destinationActions =
        confirmingDestination || pickingDestination ? (
            <>
                {confirmingDestination ? (
                    <BigAction
                        onClick={() => execute({ lpn: lpnCode, destinationCode: confirmLocation.code })}
                        loading={busy}
                    >
                        {freeDestination
                            ? label('wms.collector.confirm')
                            : `${label('wms.collector.stored_at')} ${confirmSpot}`}
                    </BigAction>
                ) : null}
                {freeDestination ? (
                    <Button
                        variant="secondary"
                        size="lg"
                        className="w-full"
                        onClick={restartDestination}
                        disabled={busy}
                    >
                        {label('wms.collector.change_destination')}
                    </Button>
                ) : null}
            </>
        ) : null;

    const footer = (
        <>
            {destinationActions}
            <div className="flex gap-2">
                <Button variant="secondary" size="lg" className="flex-1" onClick={openPause} disabled={busy}>
                    <Clock size={20} className="mr-2" />
                    {label('wms.collector.pause_action')}
                </Button>
                {/* Porta da exceção: visível em todo passo, com cara de exceção
                    (contorno âmbar, nunca o botão grande) e abrindo uma tela de
                    confirmação com motivo obrigatório. */}
                <button
                    type="button"
                    onClick={openSkip}
                    disabled={busy}
                    className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-warning-400 px-4 py-3 text-base font-semibold text-warning-text transition-colors hover:bg-warning-soft disabled:cursor-not-allowed disabled:opacity-50 dark:border-warning-700 dark:text-warning-300 dark:hover:bg-warning-900/30"
                >
                    <Warning size={20} />
                    {label('wms.collector.skip_action')}
                </button>
            </div>
            <Button variant="ghost" size="lg" className="w-full" onClick={onExit} disabled={busy}>
                <ArrowLeft size={20} className="mr-2" />
                {label('wms.collector.back_to_orders')}
            </Button>
        </>
    );

    // Código da ETIQUETA a bipar: no rack é o bloco (A-01); fixo é o próprio código.
    const scanLabelCode = stepLocation ? (IsRackLocation(stepLocation) && expectedCode ? expectedCode : stepLocation.code) : null;
    const positionCodeLine = scanLabelCode ? <CodeLine>{scanLabelCode}</CodeLine> : null;

    return (
        <>
            {flashNode}
            <StepShell stepIndex={stepIndex} stepCount={itemSteps.length} stepName={stepName} footer={footer}>
                {/* Faixa de identidade da OS: o código é o que o Supervisor
                    FALA, então ele fica visível o tempo todo — é como o
                    operador confere que está na ordem certa. */}
                <div className="flex items-center justify-between gap-2 rounded-xl bg-mono-50 px-3 py-2 dark:bg-mono-900">
                    <Mono className="text-sm font-black text-black dark:text-white">{order.code}</Mono>
                    <span className="truncate text-xs text-mono-600 dark:text-mono-400">
                        {order.type_label || order.type_code || ''}
                    </span>
                    {order.group_id ? (
                        <Badge variant="default">{label('wms.service_order.field.group')}</Badge>
                    ) : null}
                </div>


                {/* ORIGEM: só o endereço em texto — a torre aparece DEPOIS do
                    bipe do bloco (no passo do LPN), nunca antes. */}
                {step === ITEM_STEP_ORIGIN ? (
                    <>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.collector.go_to_origin')}
                        </h3>
                        <PlainAddress>{plainScan(from)}</PlainAddress>
                        {positionCodeLine}
                    </>
                ) : null}

                {step === ITEM_STEP_LPN ? (
                    <>
                        {/* Item sem origem (pallet em formação, pedido) não tem
                            "De": o pallet é achado pelo LPN. */}
                        {from ? (
                            <div className="rounded-xl bg-mono-50 px-3 py-2 text-center dark:bg-mono-900">
                                <p className="text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                    {label('wms.collector.from')}
                                </p>
                                <p className="text-base font-bold text-black dark:text-white">{plain(from)}</p>
                                <CodeLine className="text-xs">{from.code}</CodeLine>
                            </div>
                        ) : null}
                        <PlainAddress>{label('wms.collector.step_lpn_title')}</PlainAddress>
                        {/* ANTES do bipe o operador precisa saber o que vai
                            fazer com este pallet: levar inteiro, ou abrir e
                            tirar unidades. É o que muda o que ele faz com as
                            mãos — e é a diferença que o M5 não mostrava. */}
                        {fractional ? (
                            <div className="rounded-xl border-4 border-info-300 bg-info-soft px-4 py-3 text-center dark:border-info-800 dark:bg-info-900/20">
                                <p className="text-2xl font-black text-black dark:text-white">
                                    {label('wms.collector.fractional_title')}
                                </p>
                                <p className="mt-1 text-sm font-semibold text-mono-700 dark:text-mono-300">
                                    {label('wms.collector.pallet_stays')}
                                </p>
                            </div>
                        ) : (
                            <div className="rounded-xl border-4 border-success-300 bg-success-soft px-4 py-3 text-center dark:border-success-800 dark:bg-success-900/20">
                                <p className="text-2xl font-black text-black dark:text-white">
                                    {label('wms.collector.full_pallet_title')}
                                </p>
                                <p className="mt-1 text-sm font-semibold text-mono-700 dark:text-mono-300">
                                    {label('wms.collector.full_pallet_hint')}
                                </p>
                            </div>
                        )}
                        <p className="text-center text-base text-mono-600 dark:text-mono-400">
                            {fractional
                                ? label('wms.collector.fractional_lpn_hint')
                                : label('wms.collector.take_lpn_hint')}
                        </p>
                        {/* O LPN esperado NUNCA aparece (regra do dono): o
                            operador bipa a etiqueta do pallet e o SISTEMA diz
                            se é o certo — mostrar a resposta permitiria "bipar"
                            sem estar na frente do pallet. A TORRE fica: é ela
                            que diz de qual posição tirar o pallet. */}
                        {item.lpn_code ? null : (
                            <p className="text-center text-base font-medium text-warning-text dark:text-warning-300">
                                {label('wms.collector.lpn_free_choice')}
                            </p>
                        )}
                        {tower ? (
                            <BlockTower
                                rack={tower.rack}
                                bay={tower.bay}
                                positions={tower.positions}
                                targetCode={towerTargetCode}
                                label={label}
                            />
                        ) : null}
                    </>
                ) : null}

                {/* ---------- O "00" — a cabeceira do rack ---------- */}
                {step === ITEM_STEP_STAGING ? (
                    <>
                        <div className="flex items-center justify-center gap-2 rounded-xl bg-success-soft px-3 py-2 text-success-text dark:bg-success-900/30 dark:text-success-300">
                            <Package size={20} className="shrink-0" />
                            <Mono className="truncate text-sm font-bold">{lpnCode || item.lpn_code}</Mono>
                        </div>
                        <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                            {label('wms.collector.go_to_staging')}
                        </h3>
                        <PlainAddress>{`${label('wms.field.rack')} ${item.from_location_rack}`}</PlainAddress>
                        <CodeLine>{StagingStepCode(item)}</CodeLine>
                        <p className="text-center text-base text-mono-600 dark:text-mono-400">
                            {label('wms.collector.staging_hint')}
                        </p>
                    </>
                ) : null}

                {/* ---------- UNIDADES (só no fracionado) ---------- */}
                {step === ITEM_STEP_UNITS ? (
                    <>
                        <div className="rounded-xl border-4 border-info-300 bg-info-soft px-4 py-4 text-center dark:border-info-800 dark:bg-info-900/20">
                            <p className="text-5xl font-black leading-none text-black dark:text-white">
                                {`${label('wms.collector.take_units_title')} ${FormatInt(unitTarget ?? 0)}`}
                            </p>
                            <p className="mt-1 text-2xl font-black text-black dark:text-white">
                                {Number(unitTarget) === 1
                                    ? label('wms.collector.unit_word')
                                    : label('wms.collector.units_word')}
                            </p>
                            <p className="mt-2 truncate text-sm font-semibold text-mono-700 dark:text-mono-300">
                                {item.product_name || ''}
                            </p>
                            {item.sku ? <Mono className="text-xs text-mono-500">{item.sku}</Mono> : null}
                        </div>

                        {/* O CONTADOR é o que responde "já peguei quantas?" com
                            luva e barulho — uma bolinha por unidade, a próxima
                            piscando. */}
                        <div className="space-y-2 text-center">
                            <p className="text-sm font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                                {`${label('wms.collector.units_counter')}: ${FormatInt(unitsPicked)} / ${FormatInt(unitTarget ?? 0)}`}
                            </p>
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                {Array.from({ length: unitTarget ?? 0 }, (_, index) => (
                                    <span
                                        key={index}
                                        className={`h-5 w-5 rounded-full border-2 ${
                                            index < unitsPicked
                                                ? 'border-success-500 bg-success-500'
                                                : index === unitsPicked
                                                  ? 'animate-pulse border-info-500 bg-info-soft'
                                                  : 'border-mono-300 dark:border-mono-700'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>

                        {unitWaitMs > 0 ? (
                            <div className="rounded-xl bg-success-soft px-4 py-3 text-center text-success-text dark:bg-success-900/30 dark:text-success-300">
                                <p className="text-base font-bold">{label('wms.collector.unit_reserved')}</p>
                                <p className="mt-1 text-3xl font-black tabular-nums">{Math.ceil(unitWaitMs / 1000)}</p>
                                <p className="text-xs opacity-80">{label('wms.collector.unit_wait')}</p>
                            </div>
                        ) : (
                            <p className="text-center text-base text-mono-600 dark:text-mono-400">
                                {label('wms.collector.units_scan_hint')}
                            </p>
                        )}

                        <p className="text-center text-sm font-medium text-mono-500 dark:text-mono-400">
                            {label('wms.collector.pallet_stays')}
                        </p>
                    </>
                ) : null}

                {step === ITEM_STEP_DESTINATION ? (
                    <>
                        <div className="flex items-center justify-center gap-2 rounded-xl bg-success-soft px-3 py-2 text-success-text dark:bg-success-900/30 dark:text-success-300">
                            <Package size={20} className="shrink-0" />
                            <Mono className="truncate text-sm font-bold">{lpnCode || item.lpn_code}</Mono>
                            <ArrowRight size={18} className="shrink-0" />
                        </div>

                        {/* 1. BIPE — nunca com a torre: ela só existe depois
                            que o operador provou onde está (regra do dono). */}
                        {!confirmingDestination && !pickingDestination ? (
                            freeDestination ? (
                                <>
                                    <PlainAddress>{label('wms.collector.free_destination_title')}</PlainAddress>
                                    <p className="text-center text-base text-mono-600 dark:text-mono-400">
                                        {label('wms.collector.free_destination_hint')}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                                        {label('wms.collector.go_to_destination')}
                                    </h3>
                                    <PlainAddress>{plainScan(to)}</PlainAddress>
                                    {positionCodeLine}
                                </>
                            )
                        ) : null}

                        {/* 2. ESCOLHA na torre (destino livre, bipou um bloco):
                            só posição livre responde ao toque. */}
                        {pickingDestination ? (
                            <>
                                <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                                    {label('wms.collector.pick_destination_title')}
                                </h3>
                                <PlainAddress>
                                    {`${label('wms.field.rack')} ${destinationPick.rack}, ${label('wms.field.bay')} ${destinationPick.bay}`}
                                </PlainAddress>
                                <CodeLine>{destinationPick.code}</CodeLine>
                                <BlockTower
                                    rack={destinationPick.rack}
                                    bay={destinationPick.bay}
                                    positions={destinationPick.positions}
                                    label={label}
                                    onPick={handlePickDestination}
                                    pickFilter="FREE"
                                />
                            </>
                        ) : null}

                        {/* 3. CONFERÊNCIA: a posição acesa na torre (leitura) e
                            o botão grande no rodapé grava o item. */}
                        {confirmingDestination ? (
                            <>
                                <h3 className="text-center text-xl font-bold text-mono-600 dark:text-mono-300">
                                    {freeDestination
                                        ? label('wms.collector.confirm_title')
                                        : label('wms.collector.planned_position_title')}
                                </h3>
                                <PlainAddress>{plain(confirmLocation)}</PlainAddress>
                                <CodeLine>{confirmLocation.code}</CodeLine>
                                {confirmTower?.positions?.length ? (
                                    <BlockTower
                                        rack={confirmTower.rack}
                                        bay={confirmTower.bay}
                                        positions={confirmTower.positions}
                                        targetCode={confirmLocation.code}
                                        label={label}
                                    />
                                ) : null}
                            </>
                        ) : null}
                    </>
                ) : null}

                {/* Escolha e conferência do destino são toques, não bipes. */}
                {confirmingDestination || pickingDestination ? null : (
                    <ScanInput
                        label={label}
                        onScan={handleScan}
                        disabled={busy || unitWaitMs > 0}
                        focusKey={`${currentItemId}-${step}-${unitsPicked}`}
                    />
                )}
            </StepShell>
        </>
    );
};

export default ServiceOrderFlow;
